import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { SingleAuditLog, SingleAuditLogChange } from "@/types";
import { displayChangeFieldName, formatDueDate } from "@/utils/utils";
import React, { useMemo, useState } from "react";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import { ActivityChange } from "@/components/SingleTaskViewSheet/activityChange.component";
import { Button } from "@/shadcn/components/ui/button";
import { fetchTaskActivityLogs } from "@/models/tasks.model";
import CustomLoader from "@/components/CustomLoader/customLoader.component";

type SingleTaskActivityProps = {
  taskID: number;
};

const SingleTaskActivity: React.FC<SingleTaskActivityProps> = ({ taskID }) => {
  const [logsToShow, setLogsToShow] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [auditLogs, setAuditLogs] = useState<SingleAuditLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const displayedLogs = useMemo(
    () => auditLogs.slice(0, logsToShow),
    [auditLogs, logsToShow]
  );

  async function getActivityLogs() {
    setLoading(true);
    setError(null);
    setAuditLogs([]);

    const response = await fetchTaskActivityLogs({
      taskID: taskID,
    });
    if (response.rStatus === "error") {
      setError(response.rMessage || "Error fetching activity logs");
    } else {
      setAuditLogs(response.rData);
    }
    setLoading(false);
  }

  return (
    <div className="h-full">
      <h4 className="font-bold mt-3">Activity</h4>
      {!loading && !error && auditLogs.length === 0 ? (
        <Button
          variant="outline"
          className="mt-2"
          size={"sm"}
          onClick={() => {
            getActivityLogs();
          }}
        >
          Load Activity Logs
        </Button>
      ) : loading ? (
        <div className="flex justify-center items-center h-32">
          <CustomLoader size={"sm"} />
        </div>
      ) : error ? (
        <div className="text-center text-red-600 font-bold mt-2">{error}</div>
      ) : auditLogs.length === 0 ? (
        <div className="text-center font-semibold mt-2">No activity logs</div>
      ) : (
        <div className="max-h-52 mt-1 h-full overflow-auto">
          <ScrollArea className="relative overflow-hidden z-[0] h-full">
            <div className="p-1">
              {displayedLogs.map((log: SingleAuditLog, index) => (
                <div
                  key={`${log.timestamp}-${index}`}
                  className="bg-neutral-300 dark:bg-neutral-900 my-1 p-1 rounded text-sm"
                >
                  {log.type === "create" ? (
                    <div>
                      {`${log.full_name} - ${formatDueDate(
                        new Date(log.timestamp),
                        true
                      )}`}{" "}
                      Task created
                    </div>
                  ) : log.type === "copy" ? (
                    <div>
                      {`${log.full_name} - ${formatDueDate(
                        new Date(log.timestamp),
                        true
                      )}`}{" "}
                      Task copied
                    </div>
                  ) : (
                    <div>
                      <div className="mb-1">
                        {`${log.full_name} - ${formatDueDate(
                          new Date(log.timestamp),
                          true
                        )}`}{" "}
                        {/* Task updated */}
                      </div>
                      <div>
                        {/* Changes: */}
                        {log.changes.map((change: SingleAuditLogChange) => (
                          <div key={change.field} className="grid grid-cols-4">
                            <ErrorBoundary
                              fallback={
                                <div className="text-center font-bold text-red-600 text-sm m-2">
                                  Error with task activity
                                </div>
                              }
                            >
                              <div>{displayChangeFieldName(change.field)}:</div>
                              <ActivityChange
                                key={change.field}
                                change={change}
                              />
                            </ErrorBoundary>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* Show More/All */}
              <div className="flex justify-between mt-2">
                {logsToShow < auditLogs.length && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLogsToShow(logsToShow + 10);
                    }}
                  >
                    Load More
                  </Button>
                )}
                {logsToShow < auditLogs.length && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLogsToShow(auditLogs.length);
                    }}
                  >
                    Show All
                  </Button>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default SingleTaskActivity;
